import { useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Box, Button, Flex, FormControl, FormLabel, HStack, IconButton, Input, Select, SimpleGrid, Spinner, Switch,
  Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaEye, FaEyeSlash, FaFileExcel, FaFilePdf, FaPaperPlane, FaReceipt, FaRegTrashAlt } from "react-icons/fa";
import {
  adjustSettlementLine, deleteSettlementEntry, downloadSettlement, getSettlement, saveSettlementEntry,
  Settlement, SettlementEntryInput, SettlementKind, SettlementLine,
} from "../../../services/settlementService";
import { crm, Pill } from "../../../theme";
import SectionCard from "./SectionCard";
import { brl, toneColors } from "./duimp";
import { tdProps, thProps } from "./tableStyles";
import SettlementSendModal from "./SettlementSendModal";

const inputProps = {
  h: "40px",
  borderRadius: "10px",
  borderColor: crm.border,
  fontSize: "14px",
  bg: crm.surface,
  _hover: { borderColor: crm.borderStrong },
  focusBorderColor: crm.brand,
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="6px">{children}</FormLabel>
);

const date = (v?: string | null) => (v ? DateTime.fromISO(v, { setZone: true }).toFormat("dd/LL/yyyy") : "—");

const sourceTag: Record<string, { label: string; tone: keyof typeof toneColors; tip: string }> = {
  financial: { label: "Câmbio", tone: "info", tip: "Parcela de câmbio da planilha financeira" },
  numerary: { label: "DUIMP", tone: "info", tip: "Tributo pago, lançado pelo robô do Portal Único" },
  manual: { label: "Manual", tone: "neutral", tip: "Lançado nesta tela" },
};

const blank = (kind: SettlementKind): SettlementEntryInput & { id?: string } => ({ kind, paid: true });

/** Prestação de contas (fechamento): despesas × adiantamentos, conferência de pagamento/comprovante, PDF/Excel e envio. */
const SettlementPanel = ({ processId }: { processId: string }) => {
  const toast = useToast();
  const [data, setData] = useState<Settlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<SettlementEntryInput & { id?: string }>(blank("expense"));
  const [toDelete, setToDelete] = useState<SettlementLine | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [downloading, setDownloading] = useState<"" | "pdf" | "excel">("");
  const cancelRef = useRef<HTMLButtonElement>(null);

  const load = async () => {
    setLoading(true);
    try { setData(await getSettlement(processId)); }
    catch { toast({ status: "error", title: "Não foi possível carregar a prestação de contas" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [processId]);

  const fail = (e: any) => toast({ status: "error", title: "Não foi possível salvar", description: e?.message, duration: 8000, isClosable: true });

  const adjust = async (line: SettlementLine, patch: { paid?: boolean; documentId?: string | null; excluded?: boolean }) => {
    try {
      if (line.manual) {
        setData(await saveSettlementEntry(processId, line.entryId!, {
          kind: line.kind, category: line.category, description: line.description, amountBrl: line.amountBrl, date: line.date,
          paid: patch.paid ?? line.paid, documentId: patch.documentId !== undefined ? patch.documentId : line.documentId, notes: line.notes,
        }));
      } else {
        setData(await adjustSettlementLine(processId, {
          sourceType: line.sourceType, sourceId: line.sourceId!, paid: patch.paid,
          documentId: patch.documentId ?? undefined, clearDocument: patch.documentId === null, excluded: patch.excluded,
        }));
      }
    } catch (e) { fail(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...input } = draft;
      setData(await saveSettlementEntry(processId, id ?? null, {
        ...input, amountBrl: input.amountBrl == null || (input.amountBrl as any) === "" ? null : Number(input.amountBrl),
      }));
      setDraft(blank(draft.kind));
      toast({ status: "success", title: id ? "Lançamento atualizado" : "Lançamento adicionado" });
    } catch (e) { fail(e); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete?.entryId) return;
    try {
      setData(await deleteSettlementEntry(processId, toDelete.entryId));
      if (draft.id === toDelete.entryId) setDraft(blank("expense"));
    } catch (e) { fail(e); }
    finally { setToDelete(null); }
  };

  const download = async (kind: "pdf" | "excel") => {
    setDownloading(kind);
    try { await downloadSettlement(processId, kind); }
    catch { toast({ status: "error", title: "Não foi possível gerar o arquivo" }); }
    finally { setDownloading(""); }
  };

  const edit = (l: SettlementLine) => l.manual && setDraft({
    id: l.entryId!, kind: l.kind, category: l.category, description: l.description, amountBrl: l.amountBrl,
    date: l.date, paid: l.paid, documentId: l.documentId, notes: l.notes,
  });

  const balanceTone = data?.balanceDirection === "charge" ? toneColors.warning : data?.balanceDirection === "refund" ? toneColors.info : toneColors.success;
  const balanceText = !data ? "" : data.balanceDirection === "charge" ? `A cobrar ${brl(data.balance)}` : data.balanceDirection === "refund" ? `A devolver ${brl(Math.abs(data.balance))}` : "Saldo zerado";
  const pending = data ? data.missingReceipts + data.unpaid + data.withoutValue : 0;
  const categories = (data?.categories ?? []).filter((c) => c.kind === draft.kind);

  const linesTable = (title: string, lines: SettlementLine[], total: number) => (
    <Box mb="20px">
      <Flex justify="space-between" align="center" mb="8px">
        <Text fontSize="13px" fontWeight="700" color={crm.text}>{title}</Text>
        <Text fontSize="13px" fontWeight="700" color={crm.text}>{brl(total)}</Text>
      </Flex>
      {lines.length === 0 ? (
        <Text fontSize="13px" color={crm.textMuted} py="8px">Nenhum lançamento.</Text>
      ) : (
        <Box border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm} overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th {...thProps}>Data</Th>
                <Th {...thProps}>Descrição</Th>
                <Th {...thProps} isNumeric>Valor</Th>
                <Th {...thProps}>Pago</Th>
                <Th {...thProps}>Comprovante</Th>
                <Th {...thProps} w="72px" />
              </Tr>
            </Thead>
            <Tbody>
              {lines.map((l) => {
                const tag = sourceTag[l.sourceType];
                const noReceipt = !l.excluded && (!l.documentId || !l.documentName);
                return (
                  <Tr key={l.key} role="group" opacity={l.excluded ? 0.45 : 1} bg={draft.id && draft.id === l.entryId ? crm.brandSoft : undefined}>
                    <Td {...tdProps} color={crm.textMuted} whiteSpace="nowrap">{date(l.date)}</Td>
                    <Td {...tdProps} cursor={l.manual ? "pointer" : undefined} onClick={() => edit(l)}>
                      <HStack spacing="8px" align="center">
                        <Text fontWeight="600" textDecoration={l.excluded ? "line-through" : undefined}>{l.description}</Text>
                        <Tooltip label={tag.tip} hasArrow><Box><Pill {...toneColors[tag.tone]}>{tag.label}</Pill></Box></Tooltip>
                      </HStack>
                      {l.foreignAmount != null && (
                        <Text fontSize="11px" color={crm.textMuted}>
                          {l.currency} {l.foreignAmount.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          {l.rate != null ? ` × ${l.rate.toLocaleString("pt-BR", { minimumFractionDigits: 4 })}` : " · câmbio não fechado"}
                        </Text>
                      )}
                    </Td>
                    <Td {...tdProps} isNumeric fontWeight="600" color={l.amountBrl == null ? crm.warning : undefined}>{l.amountBrl == null ? "sem valor" : brl(l.amountBrl)}</Td>
                    <Td {...tdProps}>
                      <Tooltip label={!l.manual && l.paid !== l.paidBySource ? `Ajustado manualmente (origem: ${l.paidBySource ? "pago" : "não pago"})` : ""} hasArrow>
                        <Box><Switch size="sm" colorScheme="green" isChecked={l.paid} isDisabled={l.excluded} onChange={(e) => adjust(l, { paid: e.target.checked })} /></Box>
                      </Tooltip>
                    </Td>
                    <Td {...tdProps} minW="220px">
                      <Select size="sm" borderRadius="8px" borderColor={noReceipt ? crm.warning : crm.border} isDisabled={l.excluded}
                        value={l.documentName ? l.documentId ?? "" : ""}
                        onChange={(e) => adjust(l, { documentId: e.target.value || null })}>
                        <option value="">{noReceipt ? "⚠ Sem comprovante" : "—"}</option>
                        {(data?.documents ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </Select>
                    </Td>
                    <Td {...tdProps} textAlign="right" whiteSpace="nowrap">
                      {l.manual ? (
                        <IconButton aria-label="Excluir lançamento" icon={<FaRegTrashAlt />} size="xs" variant="ghost" color={crm.textFaint}
                          _hover={{ color: crm.danger, bg: "#fef2f2" }} onClick={() => setToDelete(l)} />
                      ) : (
                        <Tooltip label={l.excluded ? "Incluir no demonstrativo" : "Tirar do demonstrativo"} hasArrow>
                          <IconButton aria-label="Incluir ou excluir" icon={l.excluded ? <FaEyeSlash /> : <FaEye />} size="xs" variant="ghost"
                            color={crm.textFaint} onClick={() => adjust(l, { excluded: !l.excluded })} />
                        </Tooltip>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );

  return (
    <SectionCard
      icon={<FaReceipt />}
      title="Prestação de contas"
      subtitle={data ? `${data.modality}${data.lastSentAt ? ` · enviada em ${DateTime.fromISO(data.lastSentAt).toFormat("dd/LL/yyyy HH:mm")}${data.lastSentBy ? ` por ${data.lastSentBy}` : ""}` : " · ainda não enviada"}` : undefined}
      actions={data && (
        <>
          <Pill {...balanceTone}>{balanceText}</Pill>
          <Button size="sm" variant="outline" leftIcon={<FaFilePdf />} isLoading={downloading === "pdf"} onClick={() => download("pdf")}>PDF</Button>
          <Button size="sm" variant="outline" leftIcon={<FaFileExcel />} isLoading={downloading === "excel"} onClick={() => download("excel")}>Excel</Button>
          <Button size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} leftIcon={<FaPaperPlane />} onClick={() => setSendOpen(true)}>Enviar</Button>
        </>
      )}
    >
      {loading && !data ? (
        <Flex justify="center" py="24px"><Spinner color={crm.brand} /></Flex>
      ) : data && (
        <>
          <Flex mb="16px" p="12px 14px" borderRadius={crm.radiusSm} bg={pending ? "#fffbeb" : "#ecfdf3"} border={`1px solid ${pending ? "#fde68a" : "#bbf7d0"}`} gap="16px" wrap="wrap" fontSize="13px">
            {pending === 0 ? (
              <Text color={crm.success} fontWeight="600">Tudo conferido: todas as linhas pagas, com valor e com comprovante.</Text>
            ) : (
              <>
                <Text fontWeight="700" color={crm.warning}>Falta conferir:</Text>
                {data.missingReceipts > 0 && <Text>{data.missingReceipts} sem comprovante</Text>}
                {data.unpaid > 0 && <Text>{data.unpaid} não pago(s)</Text>}
                {data.withoutValue > 0 && <Text>{data.withoutValue} sem valor (câmbio não fechado)</Text>}
              </>
            )}
          </Flex>

          {linesTable("Despesas", data.expenses, data.totalExpenses)}
          {linesTable("Adiantamentos (numerários recebidos do cliente)", data.advances, data.totalAdvances)}

          <Box mt="8px" p="16px" bg={crm.subtle} border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm}>
            <Text fontSize="13px" fontWeight="700" color={crm.text} mb="12px">{draft.id ? "Editar lançamento" : "Novo lançamento"}</Text>
            <SimpleGrid columns={{ base: 1, md: 3, xl: 6 }} spacing="12px">
              <FormControl>
                <Label>Tipo</Label>
                <Select {...inputProps} value={draft.kind} isDisabled={!!draft.id} onChange={(e) => setDraft({ ...draft, kind: e.target.value as SettlementKind, category: null })}>
                  <option value="expense">Despesa</option>
                  <option value="advance">Adiantamento</option>
                </Select>
              </FormControl>
              <FormControl>
                <Label>Categoria</Label>
                <Select {...inputProps} value={draft.category ?? ""} onChange={(e) => setDraft({ ...draft, category: e.target.value || null })}>
                  <option value="">Selecione</option>
                  {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </Select>
              </FormControl>
              <FormControl gridColumn={{ md: "span 1", xl: "span 2" }}>
                <Label>Descrição (opcional)</Label>
                <Input {...inputProps} value={draft.description ?? ""} placeholder="usa a categoria se vazio" onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
              </FormControl>
              <FormControl>
                <Label>Valor (R$)</Label>
                <Input {...inputProps} type="number" step="0.01" value={draft.amountBrl ?? ""} placeholder={draft.kind === "advance" ? "maior que zero" : "negativo = desconto"}
                  onChange={(e) => setDraft({ ...draft, amountBrl: e.target.value === "" ? null : Number(e.target.value) })} />
              </FormControl>
              <FormControl>
                <Label>Data</Label>
                <Input {...inputProps} type="date" value={draft.date?.slice(0, 10) ?? ""} onChange={(e) => setDraft({ ...draft, date: e.target.value ? `${e.target.value}T00:00:00` : null })} />
              </FormControl>
            </SimpleGrid>
            <Flex mt="12px" justify="space-between" align="flex-end" gap="12px" wrap="wrap">
              <HStack spacing="16px" align="flex-end" flex="1" minW="260px">
                <FormControl maxW="340px">
                  <Label>Comprovante</Label>
                  <Select {...inputProps} value={draft.documentId ?? ""} onChange={(e) => setDraft({ ...draft, documentId: e.target.value || null })}>
                    <option value="">Sem comprovante</option>
                    {data.documents.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormControl>
                <FormControl w="auto" display="flex" alignItems="center" gap="8px" pb="10px">
                  <Switch colorScheme="green" isChecked={!!draft.paid} onChange={(e) => setDraft({ ...draft, paid: e.target.checked })} />
                  <Text fontSize="13px">{draft.kind === "advance" ? "Recebido" : "Pago"}</Text>
                </FormControl>
              </HStack>
              <HStack>
                <Button variant="outline" borderColor={crm.border} borderRadius="10px" onClick={() => setDraft(blank(draft.kind))}>{draft.id ? "Cancelar" : "Limpar"}</Button>
                <Button bg={crm.brand} color="white" borderRadius="10px" _hover={{ bg: crm.brandDark }} isLoading={saving} onClick={save}>{draft.id ? "Salvar alterações" : "Adicionar"}</Button>
              </HStack>
            </Flex>
            <Text fontSize="11px" color={crm.textFaint} mt="8px">
              Câmbio e tributos da DUIMP entram sozinhos. Para anexar comprovantes, suba o arquivo na aba de documentos do processo e escolha-o aqui.
            </Text>
          </Box>
        </>
      )}

      <AlertDialog isOpen={!!toDelete} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="16px">Excluir lançamento?</AlertDialogHeader>
            <AlertDialogBody fontSize="14px">"{toDelete?.description}" ({brl(toDelete?.amountBrl)}) sai da prestação de contas.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setToDelete(null)}>Cancelar</Button>
              <Button colorScheme="red" ml={3} onClick={confirmDelete}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {sendOpen && data && (
        <SettlementSendModal processId={processId} settlement={data} onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); load(); }} />
      )}
    </SectionCard>
  );
};

export default SettlementPanel;
